(() => {
  const MAX_BYTES = 10 * 1024 * 1024;
  const MAX_EDGE = 2500;
  const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

  const initRoot = (root) => {
    if (!root || root.dataset.fpAssistedReady === 'true') return;
    root.dataset.fpAssistedReady = 'true';

    const form = root.querySelector('[data-fp-assisted-form]');
    const submitBtn = root.querySelector('[data-fp-assisted-submit]');
    const statusEl = root.querySelector('[data-fp-assisted-status]');
    const expandBtn = root.querySelector('[data-fp-assisted-expand]');
    const panel = root.querySelector('[data-fp-assisted-panel]');
    if (!form || !submitBtn) return;

    const openPanel = () => {
      root.classList.add('is-open');
      if (panel) panel.hidden = false;
      if (expandBtn) {
        expandBtn.hidden = true;
        expandBtn.setAttribute('aria-expanded', 'true');
      }
    };

    expandBtn?.addEventListener('click', () => {
      openPanel();
    });

    const openFromHash = () => {
      if (window.location.hash !== '#fp-assisted-design') return;
      if (root.id !== 'fp-assisted-design') return;
      if (expandBtn && !expandBtn.disabled) openPanel();
      root.scrollIntoView({ block: 'start', behavior: 'smooth' });
    };

    openFromHash();
    window.addEventListener('hashchange', openFromHash);

    const messages = {
      image: root.dataset.errorImage,
      size: root.dataset.errorSize,
      direction: root.dataset.errorDirection,
      type: root.dataset.errorType,
      tooLarge: root.dataset.errorTooLarge,
      heic: root.dataset.errorHeic,
      fee: root.dataset.errorFee,
      generic: root.dataset.errorGeneric,
      uploading: root.dataset.statusUploading,
      feeStatus: root.dataset.statusFee,
      submit: root.dataset.submitLabel,
      submitting: root.dataset.submittingLabel,
      fileChosen: root.dataset.fileChosen
    };

    const setStatus = (message) => {
      if (!statusEl) return;
      statusEl.textContent = message || '';
    };

    const selectedVariant = () => {
      const select = document.querySelector('[data-fp-variant-select]');
      if (!select || !select.value) return { id: '', size: '', available: false };
      const option = select.options[select.selectedIndex];
      return {
        id: select.value,
        size: option ? option.textContent.trim() : '',
        available: option ? !option.disabled : false
      };
    };

    const requestId = () => {
      const rand = Math.random().toString(36).slice(2, 10);
      return `fp-ad-${Date.now()}-${rand}`;
    };

    const parseJson = async (response) => {
      const text = await response.text();
      try {
        return JSON.parse(text);
      } catch (error) {
        throw new Error(text || messages.generic);
      }
    };

    const isHeic = (file) => {
      const type = (file.type || '').toLowerCase();
      const name = (file.name || '').toLowerCase();
      return type.includes('heic') || type.includes('heif') || name.endsWith('.heic') || name.endsWith('.heif');
    };

    const isAcceptedType = (file) => {
      const type = (file.type || '').toLowerCase();
      const name = (file.name || '').toLowerCase();
      if (ACCEPTED_TYPES.includes(type)) return true;
      return name.endsWith('.jpg') || name.endsWith('.jpeg') || name.endsWith('.png') || name.endsWith('.webp');
    };

    const basename = (file) => {
      const name = (file.name || 'image').replace(/\.[^.]+$/, '');
      return name || 'image';
    };

    const loadImageElement = (file) => new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file);
      const image = new Image();
      image.onload = () => {
        URL.revokeObjectURL(url);
        resolve(image);
      };
      image.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('decode'));
      };
      image.src = url;
    });

    const decodeImage = async (file) => {
      if (typeof createImageBitmap === 'function') {
        try {
          return await createImageBitmap(file);
        } catch (error) {}
      }
      return loadImageElement(file);
    };

    const canvasToJpeg = (canvas, name) => new Promise((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (!blob) {
          reject(new Error('encode'));
          return;
        }
        resolve(new File([blob], `${name}.jpg`, { type: 'image/jpeg', lastModified: Date.now() }));
      }, 'image/jpeg', 0.86);
    });

    const drawToCanvas = (source) => {
      const width = source.naturalWidth || source.width;
      const height = source.naturalHeight || source.height;
      let nextWidth = width;
      let nextHeight = height;
      const longest = Math.max(width, height);
      if (longest > MAX_EDGE) {
        const scale = MAX_EDGE / longest;
        nextWidth = Math.round(width * scale);
        nextHeight = Math.round(height * scale);
      }
      const canvas = document.createElement('canvas');
      canvas.width = nextWidth;
      canvas.height = nextHeight;
      const context = canvas.getContext('2d');
      context.drawImage(source, 0, 0, nextWidth, nextHeight);
      if (typeof source.close === 'function') source.close();
      return canvas;
    };

    const prepareFile = async (file) => {
      if (!file) return null;

      let working = file;
      if (isHeic(file) || !file.type) {
        try {
          const drawable = await decodeImage(file);
          working = await canvasToJpeg(drawToCanvas(drawable), basename(file));
        } catch (error) {
          if (isHeic(file)) throw new Error(messages.heic);
        }
      }

      if (!isAcceptedType(working)) throw new Error(messages.type);

      const needsResize = working.size > 8 * 1024 * 1024;
      if (needsResize || working.type === 'image/jpeg' && working.size > MAX_BYTES) {
        try {
          const drawable = await decodeImage(working);
          working = await canvasToJpeg(drawToCanvas(drawable), basename(working));
        } catch (error) {}
      }

      if (working.size > MAX_BYTES) throw new Error(messages.tooLarge);
      return working;
    };

    const addLine = async (formData) => {
      const response = await fetch(root.dataset.cartAddUrl, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        credentials: 'same-origin',
        body: formData
      });
      const payload = await parseJson(response);
      if (!response.ok || payload.status) {
        throw new Error(payload.description || payload.message || messages.generic);
      }
      return payload;
    };

    const removeLine = async (key) => {
      if (!key || !root.dataset.cartChangeUrl) return;
      await fetch(root.dataset.cartChangeUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'same-origin',
        body: JSON.stringify({ id: key, quantity: 0 })
      });
    };

    const lineKey = (payload) => {
      if (!payload) return '';
      if (payload.key) return payload.key;
      if (payload.items && payload.items[0]) return payload.items[0].key || '';
      return '';
    };

    const syncVariantAvailability = () => {
      if (!root.dataset.feeVariantId) {
        submitBtn.disabled = true;
        submitBtn.setAttribute('aria-disabled', 'true');
        return;
      }
      const variant = selectedVariant();
      const available = Boolean(variant.id && variant.available);
      submitBtn.disabled = !available;
      submitBtn.setAttribute('aria-disabled', String(!available));
    };

    document.addEventListener('fp:variant:change', (event) => {
      if (!event.target || !event.target.closest('[data-fp-product-v2]')) return;
      syncVariantAvailability();
    });
    syncVariantAvailability();

    form.querySelectorAll('[data-fp-assisted-file]').forEach((input) => {
      input.addEventListener('change', () => {
        const nameEl = root.querySelector(`[data-fp-assisted-file-name="${input.dataset.fpAssistedFile}"]`);
        if (!nameEl) return;
        const file = input.files && input.files[0];
        if (!file) {
          nameEl.hidden = true;
          nameEl.textContent = '';
          return;
        }
        nameEl.hidden = false;
        nameEl.textContent = file.name;
      });
    });

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      event.stopPropagation();

      const image1 = form.image1.files[0];
      const image2 = form.image2.files[0];
      const variant = selectedVariant();
      const direction = form.querySelector('input[name="designDirection"]:checked');
      const feeVariantId = root.dataset.feeVariantId;

      if (!image1) {
        setStatus(messages.image);
        return;
      }
      if (!variant.id || !variant.available) {
        setStatus(messages.size);
        return;
      }
      if (!direction) {
        setStatus(messages.direction);
        return;
      }
      if (!feeVariantId) {
        setStatus(messages.fee);
        return;
      }

      submitBtn.disabled = true;
      submitBtn.textContent = messages.submitting;
      setStatus(messages.uploading);

      let parentKey = '';
      try {
        const prepared1 = await prepareFile(image1);
        const prepared2 = image2 ? await prepareFile(image2) : null;
        const designRequestId = requestId();
        const marketing = form.marketingOptIn && form.marketingOptIn.checked ? 'Yes' : 'No';

        const productData = new FormData();
        productData.append('id', variant.id);
        productData.append('quantity', '1');
        productData.append('properties[We Design It For You]', 'Yes');
        productData.append('properties[Product]', root.dataset.productType || '');
        if (variant.size) productData.append('properties[Size]', variant.size);
        productData.append('properties[Image 1]', prepared1, prepared1.name);
        if (prepared2) productData.append('properties[Image 2]', prepared2, prepared2.name);
        if (form.customerName.value.trim()) {
          productData.append('properties[Name]', form.customerName.value.trim());
        }
        if (form.customerNumber.value.trim()) {
          productData.append('properties[Number]', form.customerNumber.value.trim());
        }
        productData.append('properties[Design direction]', direction.value);
        if (form.instructions.value.trim()) {
          productData.append('properties[Instructions]', form.instructions.value.trim());
        }
        productData.append('properties[Marketing permission]', marketing);
        productData.append('properties[Preview]', 'WhatsApp approval required before production');
        productData.append('properties[_fp_design_request_id]', designRequestId);

        const parentLine = await addLine(productData);
        parentKey = lineKey(parentLine);

        setStatus(messages.feeStatus);
        const feeData = new FormData();
        feeData.append('id', feeVariantId);
        feeData.append('quantity', '1');
        feeData.append('properties[We Design It For You]', 'Yes');
        feeData.append('properties[_fp_design_request_id]', designRequestId);

        try {
          await addLine(feeData);
        } catch (feeError) {
          await removeLine(parentKey);
          throw new Error(messages.fee);
        }

        window.location.assign(root.dataset.cartUrl);
      } catch (error) {
        setStatus(error.message || messages.generic);
        submitBtn.textContent = messages.submit;
        syncVariantAvailability();
      }
    });
  };

  const init = () => {
    document.querySelectorAll('[data-fp-assisted-design]').forEach(initRoot);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
})();
